import { useMemo } from "react";
import { FieldValues, useController } from "react-hook-form";

import UIcon from "lib/icons/untitled_ui/UIcon";
import { useBidResponseWorkflow } from "../../../../lib/hooks/api/bid/bid_responses/useBidResponseWorkflow";
import { Select, SelectProps } from "../../../form_components/Inputs";
import { BidResponseStageOptionRenderer } from "./stageSelectUtils";
import { BidResponseStageOption, processBidResponseTransitions } from "./utils";

type FieldProps<T extends FieldValues> = Omit<SelectProps<T>, "options" | "onChange"> & {
  fillbackground?: boolean;
  onChange?: (value: string, option?: BidResponseStageOption) => void;
  preventFormValueChange?: boolean;
};

export function BidResponseStageDropdownSelect<T extends FieldValues>(props: FieldProps<T>) {
  const { data, isLoading } = useBidResponseWorkflow();

  // Use the default stage from workflow if no default value is provided
  const defaultValue = data?.transitions?.[0]?.stage?.id;

  const { field } = useController({
    ...props,
    defaultValue: props.defaultValue ?? defaultValue,
  });

  const options = useMemo(
    () => (data ? processBidResponseTransitions(data.transitions) : []),
    [data],
  );

  let backgroundColor = undefined;

  const optionCache: Record<string, BidResponseStageOption> = useMemo(
    () =>
      options
        .flatMap((group) => group.options)
        .reduce((acc, option) => ({ ...acc, [option.value]: option }), {}),
    [options],
  );

  if (props.fillbackground && props.variant === "borderless") {
    const selectedOption = optionCache[field.value];
    backgroundColor = selectedOption?.color ?? "transparent";
  }

  return (
    <Select
      {...props}
      onChange={(value) => {
        if (props.onChange) {
          props.onChange(value, optionCache[value]);
        }
      }}
      loading={isLoading}
      options={options}
      labelRender={(props) => (
        <BidResponseStageOptionRenderer
          label={props.label}
          value={String(props.value)}
          options={options}
        />
      )}
      optionRender={(oriOption) => (
        <BidResponseStageOptionRenderer
          label={oriOption.label}
          value={String(oriOption.value || "")}
          options={options}
        />
      )}
      style={{ backgroundColor }}
      preventFormValueChange={props.preventFormValueChange}
      suffixIcon={<UIcon icon="chevronDown" color="sysTextDefault" />}
    />
  );
}
